import { AxiosError } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { call, delay, put, select, take, takeEvery } from 'redux-saga/effects';
import { FeatureFlag, isFeatureEnabled } from 'Utils/FeatureFlags';
import logger from 'Utils/Logger';
import { awsRum } from 'Utils/AwsRum';
import {
  clearQueued,
  clearSendFailed,
  clearSettled,
  clearWriteFailed,
  clearWriteStarted,
  clearWriteSucceeded,
  exerciseContentFailed,
  exerciseContentLoaded,
  exerciseContentRequested,
  hydrateRequested,
  playSessionOpened,
  playSessionOpenFailed,
  playthroughCompleted,
  pointEarned,
  progressHydrated,
  selectCurrentExerciseId,
  selectLastCompletedSummary,
  selectOpenStatus,
  selectPendingClears,
  selectPlaySessionId,
  sessionCompleted,
  sessionStarted,
  sessionUnloaded,
  type PendingClear,
  type PlaySessionOpenStatus,
} from 'Reducers/exerciseProgressReducer';
import type { SessionSummary } from 'Phaser/ExerciseSessionMachine/exerciseSessionMachine.types';
import {
  closePlaySession,
  ExerciseClearRequest,
  FieldErrors,
  getExerciseProgress,
  openPlaySession,
  PlaySessionSummary,
  postExerciseClear,
  SITTING_NOT_OPENED,
} from 'Containers/ExerciseSection/exerciseProgressApi';
import { ExerciseContent, getExerciseBySlug } from 'Containers/ExerciseSection/exerciseContentApi';
import { exercisePlaythroughCompletedEvent, getPlatformInfo } from 'Actions/events';
import {
  MAX_RESENDS,
  isDeviceOffline,
  isPermanentReject,
  resendDelayMs,
} from 'Models/eventSendPolicy';
import type { ScaleProgressCell } from 'Types/ExerciseProgressTypes';

/** The app-session analytics id, minted once at startup and parked on the event
 *  reducer. Threaded onto the play-session lifecycle, the clear, and the emitted
 *  events so a whole sitting joins on one analytics grain. */
type WithEventReducer = { eventReducer: { sessionId: string } };

/**
 * Progress wiring for the exercise section (MUS-318, 7c). The saga is the relay/
 * mirror half of "React reacts to engine events" (#115): the machine emits facts,
 * this saga observes them and talks to the backend. It never drives the machine.
 *
 * The sitting's writes wait for its open: a playthrough event or a clear made while the open is
 * in flight is sent once the open answers, with the sitting's id (or without one, when the open
 * failed), so no write names a sitting the server has not minted. A clear the server still
 * refuses as "sitting not opened" is resent once, with the id the open gave.
 *
 * Dark by default: every worker no-ops unless USE_EXERCISES is on. That guard is defense in depth
 * on top of the launcher already being flag-gated.
 */

const statusOf = (err: unknown): number | undefined =>
  err instanceof AxiosError ? err.response?.status : undefined;

const isServerErrorOrNonAxios = (err: unknown): boolean =>
  !(err instanceof AxiosError) || (statusOf(err) ?? 0) > 499;

/** True when a 422 names the sitting as not opened (the write came before the open's id). */
const refusedAsNotOpened = (err: unknown): boolean =>
  err instanceof AxiosError &&
  err.response?.status === 422 &&
  (err.response.data as FieldErrors | undefined)?.errors?.play_session_id === SITTING_NOT_OPENED;

/**
 * Waits until the current sitting's open has answered, then gives the sitting's id: the minted
 * id, or null when the open failed (or no sitting is being opened).
 */
export function* awaitOpenSettled(): Generator<unknown, string | null, unknown> {
  let status = (yield select(selectOpenStatus)) as PlaySessionOpenStatus;
  while (status === 'pending') {
    yield take([playSessionOpened.type, playSessionOpenFailed.type, sessionUnloaded.type]);
    status = (yield select(selectOpenStatus)) as PlaySessionOpenStatus;
  }
  return (yield select(selectPlaySessionId)) as string | null;
}

/**
 * `sessionStarted` -> open the backend play-session for this sitting (MUS-459), with the level
 * and tier the sitting was started from. The backend mints the row and emits session-started; we
 * park the returned id so the clear, the playthrough events, the take records and the close all
 * join on it.
 *
 * Degrade, never crash: a failed open retries once (not after a 4xx, which a resend would meet
 * again), then the sitting proceeds with no play_session_id and its held writes go without one.
 * Open is minted once per sitting and the retry is keyed on not-yet-holding an id, so a
 * lost-response retry cannot mint a second sitting.
 */
export function* openExercisePlaySession(
  action: ReturnType<typeof sessionStarted>,
): Generator<unknown, void, unknown> {
  if (!isFeatureEnabled(FeatureFlag.USE_EXERCISES)) return;

  const { exerciseId, entryContext } = action.payload;
  const { sessionId } = (yield select(
    (state: WithEventReducer) => state.eventReducer,
  )) as WithEventReducer['eventReducer'];

  const body = {
    exercise_id: exerciseId,
    analytics_session_id: sessionId,
    datetime: new Date().toISOString(),
    platform: getPlatformInfo(),
    entry_context: entryContext ?? null,
  };

  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const playSessionId = (yield call(openPlaySession, body)) as string;
      yield put(playSessionOpened({ playSessionId }));
      return;
    } catch (err) {
      logger.error('[exercise-progress] play-session open failed', err);
      if (isServerErrorOrNonAxios(err)) awsRum?.recordError(err);
      const status = statusOf(err);
      if (status !== undefined && isPermanentReject(status)) break;
    }
  }
  logger.warn(
    '[exercise-progress] play-session open did not succeed; sitting proceeds without a play_session_id',
  );
  yield put(playSessionOpenFailed());
}

/**
 * `playthroughCompleted` (the slice fact, emitted for every graded take) -> post an
 * ExercisePlaythroughCompletedEvent through the events pipeline (MUS-459), once the sitting's
 * open has answered. The pipeline merges datetime / analytics_session_id / platform; we supply
 * the take's shape plus the sitting join. No-input takes never reach here.
 */
export function* postPlaythroughEvent(
  action: ReturnType<typeof playthroughCompleted>,
): Generator<unknown, void, unknown> {
  if (!isFeatureEnabled(FeatureFlag.USE_EXERCISES)) return;

  const exerciseId = (yield select(selectCurrentExerciseId)) as number | null;
  if (exerciseId == null) {
    logger.debug('[exercise-progress] playthrough with no active exercise; post skipped');
    return;
  }

  const playSessionId = (yield* awaitOpenSettled()) as string | null;
  const { cell, config, accuracy, verdict, playthroughIndex } = action.payload;

  yield put(
    exercisePlaythroughCompletedEvent({
      exercise_id: exerciseId,
      scale: cell.scale,
      tonic: cell.tonic,
      hand: config.hand,
      range_octaves: config.range_octaves,
      direction: config.direction,
      rhythm_density: config.rhythm_density,
      tempo: config.tempo,
      accuracy,
      verdict,
      playthrough_index: playthroughIndex,
      play_session_id: playSessionId ?? undefined,
    }),
  );
}

/**
 * `pointEarned` -> put the earned clear in the pending buffer (with the `client_event_id` every
 * resend repeats), once the sitting's open has answered, and send it. The engine already counted
 * the clear in-sitting (#128); the buffer is what makes the durable write survive a failed send.
 */
export function* relayEarnedClear(
  action: ReturnType<typeof pointEarned>,
): Generator<unknown, void, unknown> {
  if (!isFeatureEnabled(FeatureFlag.USE_EXERCISES)) return;

  const exerciseId = (yield select(selectCurrentExerciseId)) as number | null;
  if (exerciseId == null) {
    logger.debug('[exercise-progress] pointEarned with no active exercise; write skipped');
    return;
  }

  const { cell, config, mastered } = action.payload;
  const { sessionId } = (yield select(
    (state: WithEventReducer) => state.eventReducer,
  )) as WithEventReducer['eventReducer'];
  yield put(clearWriteStarted());
  const playSessionId = (yield* awaitOpenSettled()) as string | null;
  const clientEventId = uuidv4();
  const body: ExerciseClearRequest = {
    exercise_id: exerciseId,
    scale: cell.scale,
    tonic: cell.tonic,
    hand: config.hand,
    range_octaves: config.range_octaves,
    direction: config.direction,
    rhythm_density: config.rhythm_density,
    tempo: config.tempo,
    mastered,
    // MUS-459: the sitting join (null when the open failed) and the analytics grain
    // the backend stamps onto the point-earned + tier-completed emits.
    play_session_id: playSessionId,
    analytics_session_id: sessionId,
    client_event_id: clientEventId,
  };
  yield put(clearQueued({ clientEventId, body }));
  yield* sendPendingClear(clientEventId);
}

const findPending = (pending: PendingClear[], clientEventId: string): PendingClear | undefined =>
  pending.find((clear) => clear.clientEventId === clientEventId);

/**
 * Sends one pending clear until the server holds it or refuses it for good. A send a resend can
 * fix (no response, a 5xx, a 401 / 408 / 429) waits with backoff and goes again, up to the event
 * senders' resend limit (a send failed while offline does not count). A refusal as "sitting not
 * opened" is resent once with the sitting's id. Any other 4xx is dropped and reported. Every
 * resend carries the same `client_event_id`, so the server stores the clear once.
 */
export function* sendPendingClear(clientEventId: string): Generator<unknown, void, unknown> {
  let resentForSitting = false;
  let pending = findPending((yield select(selectPendingClears)) as PendingClear[], clientEventId);
  while (pending) {
    try {
      const serverCell = (yield call(postExerciseClear, pending.body)) as ScaleProgressCell;
      yield put(clearSettled({ clientEventId }));
      yield put(clearWriteSucceeded({ exerciseId: pending.body.exercise_id, cell: serverCell }));
      return;
    } catch (err) {
      logger.error('[exercise-progress] earned-clear write failed', err);
      const status = statusOf(err);
      if (refusedAsNotOpened(err) && !resentForSitting) {
        resentForSitting = true;
        const playSessionId = (yield* awaitOpenSettled()) as string | null;
        yield put(clearSettled({ clientEventId }));
        yield put(
          clearQueued({ clientEventId, body: { ...pending.body, play_session_id: playSessionId } }),
        );
      } else if (status !== undefined && isPermanentReject(status)) {
        yield put(clearSettled({ clientEventId }));
        yield put(clearWriteFailed({ error: err instanceof Error ? err.message : 'refused' }));
        awsRum?.recordError(err);
        return;
      } else {
        yield* awaitResend(clientEventId, pending, err);
      }
    }
    pending = findPending((yield select(selectPendingClears)) as PendingClear[], clientEventId);
  }
}

/**
 * After a send a resend can fix: count it (unless the device is offline) and wait the backoff, or
 * give the clear up once it has used every resend.
 */
function* awaitResend(
  clientEventId: string,
  pending: PendingClear,
  err: unknown,
): Generator<unknown, void, unknown> {
  const offline = isDeviceOffline();
  const failedSends = offline ? pending.failedSends : pending.failedSends + 1;
  if (failedSends > MAX_RESENDS) {
    yield put(clearSettled({ clientEventId }));
    yield put(clearWriteFailed({ error: err instanceof Error ? err.message : 'unknown' }));
    if (isServerErrorOrNonAxios(err)) awsRum?.recordError(err);
    return;
  }
  yield put(clearSendFailed({ clientEventId, countsTowardLimit: !offline }));
  yield delay(resendDelayMs(Math.max(1, failedSends)));
}

/** `hydrateRequested` -> GET the per-cell frontier and load it (server-authoritative). */
export function* hydrateProgress(
  action: ReturnType<typeof hydrateRequested>,
): Generator<unknown, void, unknown> {
  if (!isFeatureEnabled(FeatureFlag.USE_EXERCISES)) return;

  const { exerciseId } = action.payload;
  try {
    const cells = (yield call(getExerciseProgress, exerciseId)) as ScaleProgressCell[];
    yield put(progressHydrated({ exerciseId, cells }));
  } catch (err) {
    logger.error('[exercise-progress] hydrate failed', err);
    if (isServerErrorOrNonAxios(err)) awsRum?.recordError(err);
  }
}

/** `exerciseContentRequested` -> GET the exercise by slug (its id, levels, tiers and policies). */
export function* loadExerciseContent(
  action: ReturnType<typeof exerciseContentRequested>,
): Generator<unknown, void, unknown> {
  const { slug } = action.payload;
  if (!isFeatureEnabled(FeatureFlag.USE_EXERCISES)) {
    yield put(exerciseContentFailed({ slug }));
    return;
  }
  try {
    const content = (yield call(getExerciseBySlug, slug)) as ExerciseContent;
    yield put(exerciseContentLoaded({ slug, content }));
  } catch (err) {
    logger.error('[exercise-progress] exercise read failed', err);
    if (isServerErrorOrNonAxios(err)) awsRum?.recordError(err);
    yield put(exerciseContentFailed({ slug }));
  }
}

/** The sitting's summary as the close sends it: what the client counted. */
export function closeSummary(summary: SessionSummary): PlaySessionSummary {
  return {
    playthroughs: summary.playthroughs,
    verdict_counts: {
      failed: summary.verdictCounts.failed ?? 0,
      clean: summary.verdictCounts.clean ?? 0,
      flawless: summary.verdictCounts.flawless ?? 0,
    },
    points_earned: summary.pointsEarned.length,
  };
}

/**
 * `sessionCompleted` -> close the backend sitting with the client's summary, so it persists its
 * telemetry aggregate and emits tier-session-finished. A sitting ends this way when the player
 * ends it, when the play screen unmounts, and when the page is hidden for good (the close goes
 * over keepalive). Best-effort: with no sitting id there is nothing to close (the server's
 * closer ends an open sitting after two idle hours), and a failed close never blocks navigation.
 */
export function* onSessionCompleted(): Generator<unknown, void, unknown> {
  const summary = (yield select(selectLastCompletedSummary)) as SessionSummary | null;
  logger.debug('[exercise-progress] session completed', summary?.playthroughs);

  if (!isFeatureEnabled(FeatureFlag.USE_EXERCISES)) return;

  const playSessionId = (yield* awaitOpenSettled()) as string | null;
  if (playSessionId == null) return;

  const { sessionId } = (yield select(
    (state: WithEventReducer) => state.eventReducer,
  )) as WithEventReducer['eventReducer'];

  try {
    yield call(closePlaySession, playSessionId, {
      analytics_session_id: sessionId,
      datetime: new Date().toISOString(),
      platform: getPlatformInfo(),
      ...(summary ? { summary: closeSummary(summary) } : {}),
    });
  } catch (err) {
    logger.error('[exercise-progress] play-session close failed', err);
    awsRum?.recordError(err);
  }
}

export default [
  takeEvery(sessionStarted.type, openExercisePlaySession),
  takeEvery(pointEarned.type, relayEarnedClear),
  takeEvery(playthroughCompleted.type, postPlaythroughEvent),
  takeEvery(hydrateRequested.type, hydrateProgress),
  takeEvery(exerciseContentRequested.type, loadExerciseContent),
  takeEvery(sessionCompleted.type, onSessionCompleted),
];
